import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { useNavigate } from 'react-router-dom';
import MessageBox from '../components/MessageBox';
import api from '../api';
import PageHeader from '../components/PageHeader';

export default function CreateOutlet() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    cuisineType: '',
    ownerName: '',
    ownerEmail: '',
    imageUrl: '',
    isOpen: true,
    isPopular: false,
    topRated: false,
    openingHours: {
      monday: { open: '09:00', close: '22:00' },
      tuesday: { open: '09:00', close: '22:00' },
      wednesday: { open: '09:00', close: '22:00' },
      thursday: { open: '09:00', close: '22:00' },
      friday: { open: '09:00', close: '22:00' },
      saturday: { open: '09:00', close: '22:00' },
      sunday: { open: '09:00', close: '22:00' },
    },
    coordinates: {
      latitude: '',
      longitude: '',
    },
  });

  const [message, setMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpeningHoursChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleCoordinatesChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      coordinates: {
        ...prev.coordinates,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    const {
      name,
      description,
      address,
      phone,
      cuisineType,
      coordinates,
      openingHours,
      ownerEmail,
      imageUrl,
      isOpen,
      isPopular,
      topRated,
    } = formData;

    if (
      !name ||
      !description ||
      !address ||
      !phone ||
      !cuisineType ||
      !coordinates.latitude ||
      !coordinates.longitude ||
      !ownerEmail
    ) {
      setMessage({ type: 'error', text: 'Please fill all required fields.' });
      return;
    }

    try {
      const outletPayload = {
        name,
        description,
        address,
        ownerEmail: ownerEmail.trim().toLowerCase(),
        phone,
        cuisineType,
        imageUrl,
        isOpen,
        isPopular,
        topRated,
        coordinates: {
          latitude: parseFloat(coordinates.latitude),
          longitude: parseFloat(coordinates.longitude),
        },
        opening_hours: openingHours,
      };

      await api.createRestaurant(outletPayload);

      setMessage({
        type: 'success',
        text: 'Outlet created and owner assigned!',
      });

      setTimeout(() => navigate('/outlets'), 1300);
    } catch (error) {
      console.error('Error creating outlet:', error);
      setMessage({
        type: 'error',
        text:
          error?.response?.data?.message ||
          error.message ||
          '❌ Failed to create outlet.',
      });
    }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Restaurants"
        title="Create Outlet"
        subtitle="Add a restaurant outlet and connect it to a restaurant owner account."
      />

      <form onSubmit={handleSubmit} style={styles.form}>
        <label style={styles.label}>Owner Name (optional)</label>
        <input
          name="ownerName"
          value={formData.ownerName}
          onChange={handleChange}
          style={styles.input}
          placeholder="e.g. Tebogo Motsumi"
        />

        <label style={styles.label}>Owner Email</label>
        <input
          name="ownerEmail"
          value={formData.ownerEmail}
          onChange={handleChange}
          style={styles.input}
          placeholder="e.g. owner@business.co.za"
        />

        <label style={styles.label}>Outlet Name</label>
        <input
          name="name"
          value={formData.name}
          onChange={handleChange}
          style={styles.input}
          placeholder="e.g. Gourmet Kitchen"
        />

        <label style={styles.label}>Image URL (optional)</label>
        <input
          name="imageUrl"
          value={formData.imageUrl}
          onChange={handleChange}
          style={styles.input}
          placeholder="https://example.com/image.jpg"
        />

        <label style={styles.label}>Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          style={{ ...styles.input, height: 80 }}
          placeholder="e.g. Fine dining restaurant"
        />

        <label style={styles.label}>Address</label>
        <input
          name="address"
          value={formData.address}
          onChange={handleChange}
          style={styles.input}
          placeholder="e.g. 123 Main Street, City"
        />

        <label style={styles.label}>Phone Number</label>
        <input
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          style={styles.input}
          placeholder="e.g. +1234567890"
        />

        <label style={styles.label}>Cuisine Type</label>
        <input
          name="cuisineType"
          value={formData.cuisineType}
          onChange={handleChange}
          style={styles.input}
          placeholder="e.g. Italian"
        />

        <h4 style={{ marginTop: 20 }}>Opening Hours</h4>

        {Object.entries(formData.openingHours).map(([day, times]) => (
          <div key={day}>
            <label style={styles.label}>
              {day.charAt(0).toUpperCase() + day.slice(1)}
            </label>
            <div style={styles.inlineGroup}>
              <input
                type="time"
                value={times.open}
                onChange={(e) =>
                  handleOpeningHoursChange(day, 'open', e.target.value)
                }
                style={styles.inputSmall}
              />
              <span style={{ margin: '0 10px' }}>to</span>
              <input
                type="time"
                value={times.close}
                onChange={(e) =>
                  handleOpeningHoursChange(day, 'close', e.target.value)
                }
                style={styles.inputSmall}
              />
            </div>
          </div>
        ))}

        <label style={styles.label}>Coordinates</label>
        <div style={styles.inlineGroup}>
          <input
            type="number"
            step="0.0001"
            name="latitude"
            value={formData.coordinates.latitude}
            onChange={(e) =>
              handleCoordinatesChange('latitude', e.target.value)
            }
            style={styles.inputSmall}
            placeholder="Latitude"
          />
          <input
            type="number"
            step="0.0001"
            name="longitude"
            value={formData.coordinates.longitude}
            onChange={(e) =>
              handleCoordinatesChange('longitude', e.target.value)
            }
            style={styles.inputSmall}
            placeholder="Longitude"
          />
        </div>

        <button type="submit" style={styles.button}>
          Save Outlet
        </button>
      </form>

      {message && (
        <MessageBox
          type={message.type}
          message={message.text}
          onClose={() => setMessage(null)}
        />
      )}
    </Layout>
  );
}

const styles = {
  form: {
    maxWidth: 500,
    marginTop: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
  label: {
    fontWeight: 'bold',
  },
  input: {
    padding: 10,
    borderRadius: 8,
    border: '1px solid #E6D8BE',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    fontSize: 16,
  },
  inputSmall: {
    padding: 10,
    borderRadius: 8,
    border: '1px solid #E6D8BE',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    fontSize: 16,
    width: '45%',
  },
  inlineGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  button: {
    marginTop: 10,
    padding: 12,
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },
};
